// Global Cmd+K command palette — a single source of navigation that lives
// above every route. Mounted once at the CreatorRoot top level so the
// shortcut works from anywhere (home canvas, copilot chat, settings, etc.).
//
// Behavior: Cmd+K / Ctrl+K toggles. Esc + backdrop click close. Arrow keys
// step through the filtered actions, Enter selects. Focus is trapped while
// open and restored to the previously-focused element on close. Body scroll
// is locked. The palette portals to <body> so any ancestor transform can't
// trap its fixed positioning.

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import type { JSX } from 'preact';
import {
  Search,
  PenSquare,
  Settings as SettingsIcon,
  User,
  CornerDownLeft
} from 'lucide-preact';
import { t } from '../lib/i18n';
import { NAV_ITEMS, goNav, type NavId } from '../lib/nav-items';

interface Action {
  id: string;
  labelKey: string;
  hintKey: string;
  Icon: typeof Search;
  run: () => void;
}

// §S680 — destinations come from the shared nav registry so the palette can
// never disagree with the rail / tab bar / drawer on labels or targets. The
// palette shows ALL registry items plus Settings and Account. Per-destination
// hints stay palette-only copy.
const HINT_KEYS: Record<NavId, string> = {
  home: 'cmd.newHint',
  workspace: 'cmd.workspaceHint',
  pomy: 'cmd.aiHint',
  connectors: 'cmd.connectorsHint',
  guide: 'cmd.kbHint',
  help: 'cmd.helpHint'
};

const ACTIONS: Action[] = [
  ...NAV_ITEMS.map((item): Action => {
    if (item.id === 'home') {
      // "Start a new project" is an ACTION, not just the home destination:
      // when the hash is already '' (or the App instance survives the hash
      // flip from #apps/:id/edit) assigning location.hash is a silent no-op,
      // so we also dispatch the reset event App.tsx listens for. On a real
      // route change App fresh-mounts idle and the lost event is harmless.
      return {
        id: 'new-project',
        labelKey: 'cmd.new',
        hintKey: HINT_KEYS.home,
        Icon: PenSquare,
        run: () => {
          goNav('');
          window.dispatchEvent(new CustomEvent('pomela:new-project'));
        }
      };
    }
    return {
      id: item.id,
      labelKey: item.i18nKey,
      hintKey: HINT_KEYS[item.id],
      Icon: item.Icon,
      run: () => goNav(item.hash)
    };
  }),
  {
    id: 'settings',
    labelKey: 'nav.settings',
    hintKey: 'cmd.settingsHint',
    Icon: SettingsIcon,
    run: () => goNav('settings')
  },
  {
    id: 'account',
    labelKey: 'nav.account',
    hintKey: 'cmd.accountHint',
    Icon: User,
    run: () => goNav('account')
  }
];

export function CommandPalette(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Snapshot the focused element when we open so we can restore it on close.
  const lastFocused = useRef<HTMLElement | null>(null);

  // Global toggle — Cmd+K on Mac, Ctrl+K elsewhere. Listens on window so the
  // shortcut works regardless of which route is mounted.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    // §V2 — every ⌘K destination is also reachable by a visible click: the
    // rail's Search row (and any future host) opens the palette via this event.
    const onOpenEvent = (): void => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pomela:open-palette', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pomela:open-palette', onOpenEvent);
    };
  }, []);

  // Reset filter + active row each time the palette opens; focus the input;
  // lock body scroll; remember + later restore the previously focused element.
  useEffect(() => {
    if (!open) return;
    lastFocused.current = (document.activeElement as HTMLElement) ?? null;
    setQuery('');
    setActive(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      lastFocused.current?.focus?.();
    };
  }, [open]);

  // The filtered list — case-insensitive substring on the label.
  const filtered = useMemo<Action[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ACTIONS;
    return ACTIONS.filter((a) => t(a.labelKey).toLowerCase().includes(q));
  }, [query]);

  // Keep `active` clamped inside the filtered list as the user types.
  useEffect(() => {
    if (active >= filtered.length) setActive(Math.max(0, filtered.length - 1));
  }, [filtered.length, active]);

  function close(): void {
    setOpen(false);
  }

  function runAction(a: Action | undefined): void {
    if (!a) return;
    a.run();
    close();
  }

  // Arrow navigation lives on the palette container so the input still gets
  // typing keystrokes — only Up/Down/Enter/Esc are intercepted here.
  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runAction(filtered[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      // Focus trap: keep Tab cycling inside the palette.
      e.preventDefault();
    }
  }

  if (!open) return null;

  return createPortal(
    <div
      className="cmdk-root"
      role="dialog"
      aria-modal="true"
      aria-label={t('cmd.aria')}
      onKeyDown={onKeyDown}
    >
      <div className="cmdk-scrim" onClick={close} aria-hidden="true" />
      <div className="cmdk-panel" role="document">
        <div className="cmdk-search">
          <Search size={17} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            className="cmdk-input"
            placeholder={t('cmd.placeholder')}
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            aria-label={t('cmd.ariaSearch')}
            aria-controls="cmdk-list"
            aria-activedescendant={filtered[active] ? `cmdk-row-${filtered[active].id}` : undefined}
            autoComplete="off"
            autoCorrect="off"
            spellcheck={false}
          />
        </div>
        <div
          id="cmdk-list"
          ref={listRef}
          className="cmdk-list"
          role="listbox"
          aria-label={t('cmd.ariaCommands')}
        >
          {filtered.length === 0 ? (
            <p className="cmdk-empty">{t('cmdk.noMatches')}</p>
          ) : (
            filtered.map((a, i) => {
              const isActive = i === active;
              const Icon = a.Icon;
              return (
                <button
                  key={a.id}
                  id={`cmdk-row-${a.id}`}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`cmdk-row${isActive ? ' cmdk-row--active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => runAction(a)}
                >
                  <span className="cmdk-row__icon" aria-hidden="true">
                    <Icon size={16} />
                  </span>
                  <span className="cmdk-row__body">
                    <span className="cmdk-row__label">{t(a.labelKey)}</span>
                    <span className="cmdk-row__hint">{t(a.hintKey)}</span>
                  </span>
                  <span className="cmdk-row__enter" aria-hidden="true">
                    <CornerDownLeft size={13} />
                  </span>
                </button>
              );
            })
          )}
        </div>
        <div className="cmdk-foot">
          <span className="cmdk-foot__hint">
            <kbd>↑↓</kbd> navigate
          </span>
          <span className="cmdk-foot__hint">
            <kbd>↵</kbd> select
          </span>
          <span className="cmdk-foot__hint">
            <kbd>Esc</kbd> {t('cmd.close')}
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}
